import {
  describeProgress,
  describeProgressDetail,
  describeProgressParts,
  type SprintProgressSummary,
} from './sprint-progress';

/**
 * PH-37 — the done / in-progress / not-started meter on a sprint header.
 *
 * ⭐ A BAR AND A SENTENCE, NOT ONE OR THE OTHER. The bar is what makes "how much is left" readable
 *   at a glance down a list of fourteen sprints; the sentence is what makes it readable at all to
 *   somebody who cannot distinguish the segment colours, and it is what a screen reader announces.
 *   ⛔ The bar alone would encode the whole feature in colour, which is the one channel that is not
 *     always available.
 *
 * 🔴 RENDERED ONLY WHEN `sprintProgress` RETURNED A SUMMARY. It returns `null` when the API did not
 *   send the fields (an older deployment) or when the buckets do not add up — and in that case this
 *   component is not rendered at all and the header falls back to exactly what it showed before
 *   PH-37. ⛔ A zeroed bar is not the honest degradation: "0% done" is a claim, and we would not have
 *   the evidence for it.
 *
 * ⚠️ 390px FIRST. This is a THIRD line under the name and the dates, full width, because the second
 *   line is already carrying the date range and two totals. A meter squeezed onto that line would be
 *   about 40px wide, which is a decoration rather than a reading.
 */
export function SprintProgressMeter({ progress }: { progress: SprintProgressSummary }) {
  const detail = describeProgressDetail(progress);
  const summary = describeProgress(progress);
  const parts = describeProgressParts(progress);

  return (
    <span className="mt-1 flex w-full min-w-0 flex-col gap-0.5" title={detail}>
      {/**
       * ⭐ `role="img"` with a full `aria-label`, NOT a `progressbar` role. A progressbar announces ONE
       *   value, and this carries three; the label below is the whole breakdown in words, which is the
       *   thing a non-visual reader actually needs. ⛔ `aria-hidden` on the bar with the text below
       *   would work too, but then the percentage — the fastest fact on the row — is announced nowhere.
       */}
      <span
        role="img"
        aria-label={`${progress.donePercent}% done. ${summary}`}
        className="flex h-1.5 w-full overflow-hidden rounded-full bg-[color:var(--color-surface-sunken)] ring-1 ring-[color:var(--color-line)] ring-inset"
      >
        <Segment percent={pct(progress.done, progress)} className="bg-emerald-500" />
        <Segment percent={pct(progress.inProgress, progress)} className="bg-sky-500" />
        {/* ⚠️ Not-started is the REMAINDER of the track and is deliberately left unpainted — a third
            colour would make the bar read as three equal-status phases rather than as progress. */}
      </span>

      {/**
       * 🔴 PH-51 — THREE BOXES, NOT ONE, AND ONLY THE FIRST MAY BE CLIPPED. At a measured 390px the
       *   single truncating span cut the sentence to `… · 3 op…`, keeping the count and dropping the
       *   words that say what the count is. `truncate` sits on the figures alone; the qualifier and
       *   the scope marker are `shrink-0`, so the flex row spends its overflow budget on the half
       *   that can afford it. ⛔ Wrapping to a second line instead would push every sprint row taller
       *   on the phone — and this is already the third line under the name.
       */}
      <span className="flex min-w-0 items-baseline gap-1 text-[11px] text-[color:var(--color-ink-muted)]">
        <span className="min-w-0 truncate">{parts.head}</span>

        {/* ⚠️ Given its own colour as well as its own box: at this size the difference between "17
            done" and "3 unsized" is the difference between a report and a warning. */}
        {parts.unestimated ? (
          <span className="shrink-0 font-medium text-amber-700 dark:text-amber-400">
            {parts.unestimated}
          </span>
        ) : null}

        {/* ⭐ The visible `*`, matching what the header already does for a whole-sprint number. The
            tooltip says the same thing, and the tooltip is unreachable on a phone. */}
        {progress.scope === 'whole-sprint' ? (
          <span className="shrink-0 italic">* whole sprint</span>
        ) : null}
      </span>
    </span>
  );
}

/**
 * A bucket's share of the track, on the SAME basis the percentage uses.
 * ⛔ Not always points: a sprint where nothing is estimated has zero total points, and a points-based
 *   track would be entirely empty while the team finished every ticket. `sprintProgress` already made
 *   that choice once and recorded it as `basis`; this reads it rather than deciding again.
 */
function pct(bucket: { count: number; points: number }, progress: SprintProgressSummary): number {
  const value = progress.basis === 'points' ? bucket.points : bucket.count;
  const total = progress.basis === 'points' ? progress.total.points : progress.total.count;
  if (total <= 0) return 0;
  return (value / total) * 100;
}

function Segment({ percent, className }: { percent: number; className: string }) {
  if (percent <= 0) return null;
  return <span className={className} style={{ width: `${percent}%` }} />;
}
